// eslint-disable-next-line
import React, { useReducer, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getStudentStatuses, type GetStudentStatusesResult } from '../../../../common/utility';
import { localStateReducer } from '../../../../../../utils/common';
import ReloadButton from '../../../../common/ReloadButton';
import Timestamps from '../../../../common/Timestamps';
import StudentRosterColumn from './StudentRosterColumn';
import { StudentRosterHeader } from './StudentRosterHeader';
import StudentRosterDirectionsAP from './StudentRosterDirections/StudentRosterDirectionsAP';
import './Attendance.scss';
import { useStateValue } from '../../../../../../context/AppContext';
import { isInSchoolAPEvents, isInSchoolEvent } from '../../../../../../utils/event';
import StudentRosterDirectionsSAT from './StudentRosterDirections/StudentRosterDirectionsSAT';
import { StudentRosterCell } from './StudentRosterCell';
import { ResizeContext } from '../../../../../../context/ResizeContext';

interface StudentRosterMobileProps {
  // eslint-disable-next-line
  localState: any;
  studentStatuses: GetStudentStatusesResult;
  // eslint-disable-next-line
  orgEvent: any;
  toggleAccordions: (accordionName: string) => void;
}
function StudentRosterMobile({ localState, studentStatuses, toggleAccordions, orgEvent }: StudentRosterMobileProps) {
  return (
    <div className='student-roster-swimLane-wrapper row-eq-height row px-0 mx-0'>
      <StudentRosterColumn
        columnType={'preRostered'}
        emptyStateText={
          'Students are listed here only if they’re assigned to your room in Test Day Toolkit before test day and haven’t entered the room code yet.'
        }
        headerText='ASSIGNED'
        studentStatusArray={studentStatuses?.preRosteredStudents}
        toggleVisible={toggleAccordions}
        visible={localState.accordion.preRostered}
      />
      <StudentRosterColumn
        columnType={'joined'}
        emptyStateText={
          isInSchoolEvent(orgEvent)
            ? 'Students are listed here after they enter the room code and before they complete check-in.'
            : 'Students are listed here after they enter the room code and before they finish checking in to the app.'
        }
        headerText='ENTERED ROOM CODE'
        studentStatusArray={studentStatuses?.joinedStudents}
        toggleVisible={toggleAccordions}
        visible={localState.accordion.joined}
      />
      <StudentRosterColumn
        columnType={'ready'}
        emptyStateText={'Students are listed here when they complete check-in.'}
        headerText='READY TO TEST'
        studentStatusArray={studentStatuses?.readyStudents}
        toggleVisible={toggleAccordions}
        visible={localState.accordion.ready}
      />
    </div>
  );
}

interface StudentRosterDesktopProps {
  // eslint-disable-next-line
  localState: any;
  studentStatuses: GetStudentStatusesResult;
  // eslint-disable-next-line
  orgEvent: any;
  toggleAccordions: (accordionName: string) => void;
}

function StudentRosterDesktop({ localState, studentStatuses, toggleAccordions, orgEvent }: StudentRosterDesktopProps) {
  const maxRows = Math.max(
    studentStatuses?.preRosteredStudents.length,
    studentStatuses?.joinedStudents.length,
    studentStatuses?.readyStudents.length
  );

  return (
    <table className='w-100 student-roster-table' aria-label='Student Roster'>
      <thead>
        <tr>
          <StudentRosterHeader
            columnType={'preRostered'}
            headerText='ASSIGNED'
            studentStatusArray={studentStatuses?.preRosteredStudents}
            toggleVisible={toggleAccordions}
            visible={localState.accordion.preRostered}
          ></StudentRosterHeader>

          <StudentRosterHeader
            columnType={'joined'}
            headerText='ENTERED ROOM CODE'
            studentStatusArray={studentStatuses?.joinedStudents}
            toggleVisible={toggleAccordions}
            visible={localState.accordion.joined}
          ></StudentRosterHeader>

          <StudentRosterHeader
            columnType={'ready'}
            headerText='READY TO TEST'
            studentStatusArray={studentStatuses?.readyStudents}
            toggleVisible={toggleAccordions}
            visible={localState.accordion.ready}
          ></StudentRosterHeader>
        </tr>
      </thead>
      <tbody className='student-roster-body--bkg'>
        {Array.from({
          length: maxRows,
        }).map((_, idx) => {
          return (
            <tr key={idx}>
              <StudentRosterCell
                columnType={'preRostered'}
                studentStatusArray={studentStatuses?.preRosteredStudents}
                emptyStateText={
                  'Students are listed here only if they’re assigned to your room in Test Day Toolkit before test day and haven’t entered the room code yet.'
                }
                index={idx}
                maxRows={maxRows}
                cellClassName='student-roster-table-cell--border-right'
              ></StudentRosterCell>

              <StudentRosterCell
                columnType={'joined'}
                emptyStateText={
                  isInSchoolEvent(orgEvent)
                    ? 'Students are listed here after they enter the room code and before they complete check-in.'
                    : 'Students are listed here after they enter the room code and before they finish checking in to the app.'
                }
                studentStatusArray={studentStatuses?.joinedStudents}
                index={idx}
                maxRows={maxRows}
                cellClassName='student-roster-table-cell--border-right'
              ></StudentRosterCell>
              <StudentRosterCell
                columnType={'ready'}
                emptyStateText={'Students are listed here when they complete check-in.'}
                studentStatusArray={studentStatuses?.readyStudents}
                index={idx}
                maxRows={maxRows}
                cellClassName='student-roster-table-cell--no-border'
              ></StudentRosterCell>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default function StudentRoster({
  refetch = () => {},
  room = {},
  rosterHeading = '',
  showDirectionInfo = true,
  showInpage = false,
  lastUpdated = new Date().toISOString(),
}) {
  const { orgEvent } = useStateValue();

  const windowSize = useContext(ResizeContext);

  useEffect(() => {
    setLocalState({
      isMobile: windowSize.mobile,
    });
  }, [windowSize.mobile]);

  const studentStatuses = getStudentStatuses(room?.students);
  // start - recall user's accordion visibility preferences from sessionStorage
  const getSavedAccordionState = () => {
    let accordions = {};

    try {
      accordions = JSON.parse(sessionStorage.getItem('rosterAccordions'));
      accordions.preRostered = accordions?.preRostered || false;
      accordions.ready = accordions?.ready || false;
      accordions.joined = accordions?.joined || false;
      // eslint-disable-next-line
    } catch (e) {
      accordions = {
        preRostered: false,
        joined: false,
        ready: false,
      };
    }

    return accordions;
  };

  // accordion state
  const [localState, setLocalState] = useReducer(localStateReducer, {
    isMobile: windowSize.mobile,
    accordion: getSavedAccordionState(),
    lastUpdated,
  });

  // swap the state of the accordion that was clicked; set all others to false
  function toggleAccordions(accordionName = '') {
    const newAccordions = Object.keys(localState.accordion).reduce((prev, cur) => {
      return {
        ...prev,
        [cur]: cur === accordionName ? !localState.accordion[cur] : false,
      };
    }, {});

    sessionStorage.setItem('rosterAccordions', JSON.stringify(newAccordions));

    setLocalState({
      accordion: newAccordions,
    });
  }
  // end accordion code

  function refreshData() {
    setLocalState({
      lastUpdated: new Date().toISOString(),
    });
    refetch();
  }

  return (
    <>
      <div className='mb-4 display-flex justify-content-between align-items-center flex-sm-column-only flex-xs-column-only'>
        <div
          role='heading'
          aria-level='1'
          data-automation='wizard-student-roster-heading'
          className='wizard-heading mb-0 flex-grow-1 align-self-sm-start-only align-self-xs-start-only'
          tabIndex='-1'
          name='student-roster'
          id={showInpage ? 'student-roster' : 'wizard-main-heading'}
        >
          {rosterHeading}
        </div>
        <div className='cb-align-center cb-width-xs-100-up cb-width-md-auto-up cb-margin-sm-down-top-16' role='status'>
          {!!lastUpdated && (
            <Timestamps
              className='cb-font-size-xsmall cb-margin-sm-down-8'
              id='StdRosterReloadTimeStamp'
              updatedAt={localState.lastUpdated}
            />
          )}
          <ReloadButton
            refetch={refreshData}
            buttonStyle='secondary'
            text='Refresh Data'
            reloadButtonID='StdRosterReloadData'
            reloadTimeStampID='StdRosterReloadTimeStamp'
          />
        </div>
      </div>
      <div className='cb-white-bg cb-box-shadow-light'>
        {isInSchoolAPEvents(orgEvent) ? (
          <StudentRosterDirectionsAP showDirectionInfo={showDirectionInfo} />
        ) : (
          <StudentRosterDirectionsSAT showDirectionInfo={showDirectionInfo} />
        )}
      </div>
      {!localState.isMobile ? (
        <StudentRosterDesktop
          localState={localState}
          studentStatuses={studentStatuses}
          toggleAccordions={toggleAccordions}
          orgEvent={orgEvent}
        ></StudentRosterDesktop>
      ) : (
        <StudentRosterMobile
          localState={localState}
          studentStatuses={studentStatuses}
          toggleAccordions={toggleAccordions}
          orgEvent={orgEvent}
        ></StudentRosterMobile>
      )}
    </>
  );
}

StudentRoster.propTypes = {
  lastUpdated: PropTypes.string,
  refetch: PropTypes.func,
  room: PropTypes.object,
  showDirectionInfo: PropTypes.bool,
  showInpage: PropTypes.bool,
  rosterHeading: PropTypes.string,
};
